/**
 * „Vyberte si svůj styl pobytu" (verze 2, podle návrhu) — fotkové karty: fotka,
 * patkový titulek, jedna věta a odkaz „Zobrazit …" na podrobnou kartu.
 * Dvě skupiny (Robin 24. 10.): Ubytování = penzion + tiny house,
 * Rybolov = rybářské chatky, bivak, karavan. Rezervuje se z vyhledávací lišty,
 * z podrobné karty nebo z menu.
 */
import { useWebContent } from '../lib/webPrices'
import { assetUrl } from '../lib/api'

type Style = { unit: string; title: string; tagline: string; link: string; image: string }
type StyleGroup = { key: 'ubytovani' | 'rybolov'; title: string; href: string; linkLabel: string; span: string; cols: string; items: Style[] }

const GROUPS: StyleGroup[] = [
  {
    key: 'ubytovani', title: 'Ubytování', href: '#ubytovani', linkLabel: 'Zobrazit ubytování',
    span: 'lg:col-span-2', cols: 'sm:grid-cols-2',
    items: [
      { unit: 'penzion',    title: 'Penzion',    tagline: 'Komfortní pokoje přímo u jezera',  link: 'Zobrazit pokoje',     image: '/img/penzion_01_zakouti_kreslo.jpg' },
      { unit: 'tiny-house', title: 'Tiny house', tagline: 'Sauna, terasa a hvězdy nad vodou', link: 'Zobrazit tiny house', image: '/img/tinyhouse_01_sauna_hottub.jpg' },
    ],
  },
  {
    key: 'rybolov', title: 'Rybolov', href: '#rybolov', linkLabel: 'Zobrazit rybolov',
    span: 'lg:col-span-3 lg:border-l lg:border-ink-900/[0.08] lg:pl-6', cols: 'sm:grid-cols-3',
    items: [
      { unit: 'chatka',  title: 'Rybářské chatky', tagline: 'Klid, příroda a rybaření na dosah', link: 'Zobrazit chatky',   image: '/img/chatka_01_chatka_sunset.jpg' },
      { unit: 'bivak',   title: 'Bivak',           tagline: 'Autentický rybářský zážitek',       link: 'Zobrazit možnosti', image: '/img/bivak_01_zapad.webp' },
      { unit: 'karavan', title: 'Karavan',         tagline: 'Svoboda a pohodlí v přírodě',       link: 'Zobrazit stání',    image: '/img/karavan_01_zapad.webp' },
    ],
  },
]

const SERIF = { fontFamily: "'Instrument Serif',Georgia,serif", fontWeight: 400 } as const

export function StyleCards() {
  const content = useWebContent()
  return (
    <section className="pt-4 pb-10 md:pt-6 md:pb-14">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="h-eyebrow">Ubytování &amp; rybolov v Sobáčově</p>
            <h2 className="mt-2 text-[34px] md:text-[42px] leading-[1.05] text-ink-900 tracking-tight" style={SERIF}>
              Vyberte si svůj styl pobytu
            </h2>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-x-6 gap-y-8">
          {GROUPS.map((g) => (
            <div key={g.key} className={g.span}>
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <h3 className="text-[22px] leading-none text-ink-900 tracking-tight" style={SERIF}>{g.title}</h3>
                <a href={g.href} className="text-[12px] font-semibold text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
                  {g.linkLabel} <span aria-hidden="true">→</span>
                </a>
              </div>
              <div className={`grid grid-cols-1 ${g.cols} gap-4`}>
                {g.items.map((s) => {
                  const img = assetUrl(content[`unit_image_${s.unit}`] as string) || s.image
                  const href = `#ubytovani-${s.unit}`
                  return (
                    <article key={s.unit} className="card overflow-hidden flex flex-col group">
                      <a href={href} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" aria-label={`${s.link}: ${s.title}`}>
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img src={img} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                          {/* cenový štítek záměrně ne — čisté karty jako v návrhu; cena je v liště a na podrobné kartě */}
                        </div>
                      </a>
                      <div className="p-4 flex-1 flex flex-col">
                        <h4 className="text-[24px] leading-tight text-ink-900 tracking-tight" style={SERIF}>{s.title}</h4>
                        <p className="mt-1 text-[13px] text-ink-700 leading-snug">{(content[`style_tagline_${s.unit}`] as string) || s.tagline}</p>
                        <a href={href} className="mt-3 text-[12px] font-bold text-brand-600 hover:text-brand-700 inline-flex items-center gap-1">
                          {s.link} <span aria-hidden="true">→</span>
                        </a>
                      </div>
                    </article>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
